import { useAuth } from '../../auth/useAuth'
import { useEffect, useRef, useState } from 'preact/hooks'
import { authOrganizationApi } from '@/api/authOrganization'
import { NO_ORGANIZATION_ACCESS_ERROR } from '@/auth/AuthContext'

export function useSignInLogic() {
  const assetsBaseUrl = import.meta.env.VITE_ASSETS_BASE_URL
  const logoUrl = `${assetsBaseUrl}/assets/white-orbital-x.png`
  const logoColorUrl = `${assetsBaseUrl}/assets/orbital-x-logo.png`
  const { login, isAuth0Ready, error: authenticationError } = useAuth()
  const loginRef = useRef(login)
  loginRef.current = login
  const startedRef = useRef(false)
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorModalConfig, setErrorModalConfig] = useState<{
    title: string
    message: string
  } | null>(null)

  // Auto-start login when an invitation link is opened to avoid losing params on reroute
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const hasInvitation = !!urlParams.get('invitation')
    const hasOrganization = !!urlParams.get('organization')

    if ((hasInvitation || hasOrganization) && !startedRef.current && isAuth0Ready) {
      startedRef.current = true

      const invitationParams = {
        invitation: urlParams.get('invitation') || undefined,
        organization: urlParams.get('organization') || undefined,
        organization_name: urlParams.get('organization_name') || undefined,
        login_hint: urlParams.get('email') || undefined
      }

      loginRef.current(invitationParams).catch(() => {
        startedRef.current = false
      })
    }
  }, [isAuth0Ready])

  const GENERIC_ERROR = {
    title: 'Something Went Wrong',
    message: 'We encountered an error processing your request. Please try again later.'
  }

  // Auth0 turns a non-member away before our API is ever reached, so this screen is the only place
  // the person can be told anything at all.
  //
  // The wording has to hold for everybody Auth0 refuses, not just the case that prompted it. Access
  // denied covers somebody who never accepted an invitation, somebody whose access was withdrawn, and
  // somebody a rule blocked — the SPA cannot tell them apart, and cannot see whether an invitation is
  // outstanding. So it states the fact and names one way forward without asserting which case this is.
  const NO_ORGANIZATION_ACCESS_MODAL = {
    title: 'You Cannot Sign In Yet',
    message:
      'This account does not have access to OrbitalX yet. If you were sent an invitation, open it and use the link inside rather than signing in here. Otherwise ask your administrator to invite you.'
  }

  const showError = (config: { title: string; message: string }) => {
    setErrorModalConfig(config)
    setShowErrorModal(true)
  }

  // The failure happens during the Auth0 redirect callback, long before this screen mounts, so it
  // arrives as provider state rather than an event. The ref keeps a dismissed modal dismissed: the
  // error stays in provider state until the next sign-in attempt replaces it.
  const surfacedErrorRef = useRef<string | null>(null)
  useEffect(() => {
    if (!authenticationError || surfacedErrorRef.current === authenticationError) return
    surfacedErrorRef.current = authenticationError

    showError(authenticationError === NO_ORGANIZATION_ACCESS_ERROR ? NO_ORGANIZATION_ACCESS_MODAL : GENERIC_ERROR)
  }, [authenticationError])

  const handleSubmit = async (e: Event) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      const trimmed = email.trim()
      if (!trimmed) {
        setError('Email is required')
        setIsSubmitting(false)
        return
      }
      const resp = await authOrganizationApi.getOrgIdByEmail(trimmed)

      if (resp.status === 404) {
        showError({
          title: 'Domain Not Found',
          message:
            "It doesn't look like your company is registered with us yet. If you think this is an error, please reach out to our support team for assistance."
        })
        setIsSubmitting(false)
        return
      }

      if (resp.error || resp.status !== 200) {
        showError(GENERIC_ERROR)
        setIsSubmitting(false)
        return
      }

      let orgId: string | undefined
      if (resp.data) {
        orgId = String(resp.data).trim()
      }
      await login({ organization: orgId ?? undefined, login_hint: trimmed, prompt: 'login' })
    } catch {
      showError(GENERIC_ERROR)
    } finally {
      setIsSubmitting(false)
    }
  }

  return {
    assetsBaseUrl,
    logoUrl,
    logoColorUrl,
    email,
    setEmail,
    isSubmitting,
    error,
    handleSubmit,
    showErrorModal,
    setShowErrorModal,
    errorModalConfig
  }
}
